import type { ComponentProps, ReactNode } from 'react';
import { createContext, useEffect } from 'react';
import { RouterProvider } from 'react-aria-components';

import { ChainSvgDefs } from '@infinex/ui-assets';
import { colors } from '@infinex/ui-constants';
import { BreakpointProvider } from '@infinex/ui-styles';

import { EmblemSvgDefs } from '../emblem';
import { Toaster } from '../toast';

// not sure why this isn't exported from react-aria-components…
type RouterProviderProps = ComponentProps<typeof RouterProvider>;

export type TaurusProviderProps = {
  /**
   * The contents of the application.
   */
  children: ReactNode;
  /**
   * Provides a client-side router to all descendant component links, enabling
   * client-side navigation.
   */
  router?: Pick<RouterProviderProps, 'navigate' | 'useHref'>;
  /**
   * Adjusts various implementation details depending on the render context.
   * Used for development tools like Storybook and Playroom.
   *
   * @default 'application'
   */
  viewMode?: 'application' | 'docs' | 'playroom' | 'story';
};

/**
 * Provides the necessary dependencies for Taurus components to function
 * properly.
 *
 * [Taurus docs: `TaurusProvider`](https://ui-sb.app.infinex.dev/?path=/docs/components-taurusprovider--docs)
 */
export function TaurusProvider(props: TaurusProviderProps) {
  const { children, router, viewMode = 'application' } = props;
  const includeSingletons = useIncludeSingletons(viewMode);

  let result = (
    <TaurusContext.Provider value={viewMode}>
      <BreakpointProvider>
        {viewMode !== 'application' && <RootStyle />}
        {includeSingletons && <ChainSvgDefs />}
        {includeSingletons && <EmblemSvgDefs />}
        {includeSingletons && <Toaster />}
        {children}
      </BreakpointProvider>
    </TaurusContext.Provider>
  );

  if (router) {
    result = <RouterProvider {...router}>{result}</RouterProvider>;
  }

  return <div data-taurus-root>{result}</div>;
}

// Context
// -----------------------------------------------------------------------------

type ViewMode = Extract<TaurusProviderProps['viewMode'], string>;
export const TaurusContext = createContext<ViewMode>('application');

// Utils
// -----------------------------------------------------------------------------

/**
 * Make sure the root element has the correct background colour when rendered
 * within a Playroom or Storybook iframe.
 */
function RootStyle() {
  return (
    <style>{`:root { background-color: ${colors.background.canvas}; }`}</style>
  );
}

let docsStoryCount = 0;
function useIncludeSingletons(viewMode: ViewMode) {
  // reset when navigating
  useEffect(() => {
    return () => {
      docsStoryCount = 0;
    };
  }, []);

  // only include singletons on the first docs story
  if (viewMode === 'docs') {
    return ++docsStoryCount === 1;
  }

  // always include singletons inside a single story, or when outside of
  // storybook context (e.g. typical product usage)
  return true;
}
